import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFontAwesome } from '@fortawesome/free-regular-svg-icons';
import { faCircleArrowLeft, faCircleArrowRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import {fetchPodSqueezeAPI} from '../../utils/apiutils'

interface TranscriptEmbedded {
  id: string;
  asset: string;
}

const TranscriptEmbedded = (props: TranscriptEmbedded) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [text, setText] = useState('');
  const [matches, setMatches] = useState<number[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const { id, asset } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEmbeddedData();
  }, []);

  const getTranscriptionWithSpeakers = (transcription: string, speakers: any) => {
    let newTranscription = transcription;
    Object.keys(speakers).forEach((speakerId) => {
      newTranscription = newTranscription.replaceAll(
        `${speakerId.replace('_', ' ').replace('speaker', 'Speaker')}`,
        speakers[speakerId]
      );
    });
    return newTranscription;
  };

  const fetchEmbeddedData = async () => {
    try {
      const data = await fetchPodSqueezeAPI('/podsqueeze', 'POST', {
        id: id,
        type: asset,
        mode: "get_embedded_results"
      });
  
      if (asset === "transcript") {
        const transcript = data.result.transcript;
        const speakers = data.result.speakers;
        setText(getTranscriptionWithSpeakers(transcript, speakers));
      }
    } catch (error) {
      console.error("Error fetching embedded data: ", error);
    }
  };
  
  

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentMatchIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && matches.length > 0) {
      handleNextMatch();
    }
  };

  const highlightedText = useMemo(() => {
    if (!searchTerm) {
      setMatches([]);
      return text;
    }

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const matchesArray: number[] = [];
    const highlighted = text.replace(regex, (match, offset) => {
      matchesArray.push(offset);
      return `<mark>${match}</mark>`;
    });

    setMatches(matchesArray);
    return highlighted;
  }, [searchTerm, text]);

  useEffect(() => {
    if (matches.length > 0 && containerRef.current) {
      const targetElement = containerRef.current.querySelectorAll('mark')[currentMatchIndex];
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentMatchIndex, matches]);

  const handlePreviousMatch = () => {
    setCurrentMatchIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : matches.length - 1));
  };

  const handleNextMatch = () => {
    setCurrentMatchIndex(prevIndex => (prevIndex < matches.length - 1 ? prevIndex + 1 : 0));
  };

  return (
    <div className="bg-white">
    <div className="bg-zinc-100 fixed top-0 left-0 right-0 border-b z-10 px-8 py-5">
        <FontAwesomeIcon className="mr-4" icon={faSearch}></FontAwesomeIcon>
        <input
          type="text"
          placeholder="Search transcript..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          className="p-2 border rounded w-1/2"
        />
        {matches.length > 0 && (
          <div className="mt-3">
            <button onClick={handlePreviousMatch} className="mx-0"><FontAwesomeIcon className="mr-2" icon={faCircleArrowLeft}></FontAwesomeIcon></button>
            <span>{currentMatchIndex + 1}/{matches.length}</span>
            <button onClick={handleNextMatch} className="mx-2"><FontAwesomeIcon className="mr-2" icon={faCircleArrowRight}></FontAwesomeIcon></button>
          </div>
        )}
      </div>
    <div className="text-center p-8">      
      <div ref={containerRef} className="text-lg pt-24 text-left prose-lg rounded-xl lg:px-12 overflow-y-auto max-h-screen">
        <ReactMarkdown
          rehypePlugins={[remarkGfm, rehypeRaw]}
          children={highlightedText}
          components={{
            a: ({ href, children }) => (
              <a href={href} style={{ color: '#643ADA' }}>
                {children}
              </a>
            ),
            h1: ({ children }) => (
              <h1 style={{ fontSize: '40px', marginTop: '80px', fontWeight: '800' }}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 style={{ fontSize: '28px', marginTop: '56px', fontWeight: '800' }}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 style={{ fontSize: '24px', marginTop: '24px', fontWeight: '800' }}>
                {children}
              </h3>
            ),
            ul: ({ children }) => (
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol style={{ listStyleType: 'decimal', paddingLeft: '20px' }}>
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: '8px' }}>
                {children}
              </li>
            ),
          }}
        />
      </div>
    </div>
  </div>
  );
};

export default TranscriptEmbedded;
